import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../sync/api';
import { Window } from '../ui/Window';
import { BoardPage } from './BoardPage';

interface ShareInfo {
  board: { id: string; title: string };
  role: 'editor' | 'viewer';
  needsPassword: boolean;
  unlocked: boolean;
}

type State =
  | { kind: 'loading' }
  | { kind: 'password'; title: string; error: string | null; busy: boolean }
  | { kind: 'ready'; boardId: string }
  | { kind: 'error'; message: string };

/** Opens a share link: unlocks with a password when needed, then shows the board. */
export function SharePage({ token, view }: { token: string; view: string | null }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [password, setPassword] = useState('');

  useEffect(() => {
    let alive = true;
    api
      .get<ShareInfo>(`/share/${encodeURIComponent(token)}`)
      .then((info) => {
        if (!alive) return;
        setState(info.unlocked ? { kind: 'ready', boardId: info.board.id } : { kind: 'password', title: info.board.title, error: null, busy: false });
      })
      .catch((e: unknown) => {
        if (alive) setState({ kind: 'error', message: e instanceof ApiError ? e.message : 'Could not open this link' });
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const unlock = async (e: FormEvent) => {
    e.preventDefault();
    if (state.kind !== 'password') return;
    setState({ ...state, busy: true, error: null });
    try {
      const info = await api.post<ShareInfo>(`/share/${encodeURIComponent(token)}/unlock`, { password });
      setState({ kind: 'ready', boardId: info.board.id });
    } catch (err) {
      setState({ ...state, busy: false, error: err instanceof Error ? err.message : String(err) });
    }
  };

  if (state.kind === 'ready') return <BoardPage key={state.boardId} boardId={state.boardId} remote view={view} />;

  return (
    <main className="desktop">
      <Window title={state.kind === 'password' ? state.title : 'VDS'}>
        {state.kind === 'loading' && <p>Opening shared board…</p>}
        {state.kind === 'error' && <p className="bad">{state.message}</p>}
        {state.kind === 'password' && (
          <form className="form" onSubmit={unlock}>
            <p className="form-hint">This board is password protected.</p>
            <label>
              <span>Password</span>
              <input type="password" autoFocus required value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            {state.error && <div className="form-error">{state.error}</div>}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={state.busy}>
                {state.busy ? 'Checking…' : 'Open board'}
              </button>
            </div>
          </form>
        )}
      </Window>
    </main>
  );
}
